import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext.jsx';
import { activateShopifyStore } from '../../libs/fetch.js';
import Loading from '../../components/Loading.jsx';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

export default function ConnectShopifySuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loading, organization, profile } = useAuthContext();
  const [error, setError] = useState(null);

  useEffect(() => {
    // Wait for organization to load
    if (loading || !organization) {
      return;
    }

    const handleCallback = async () => {
      try {

        // Extract Shopify OAuth parameters from URL
        let shop = searchParams.get('shop');

        // Validate required parameters
        if (!shop) {
          setError('Missing shop parameter. Please try installing the app again from Shopify.');
          return;
        }

        // If user is not logged in, redirect to signin with return URL
        // After login, user will be redirected back here with the same params
        if (!organization) {
          const returnUrl = `/connect-shopify-success?${searchParams.toString()}`;
          navigate(`/signin?returnTo=${encodeURIComponent(returnUrl)}`, { replace: true });
          return;
        }

        if (profile.primary_organization_id !== organization.id) {
          setError('You are not authorized to connect a store your non-primary organization.');
          return;
        }
        // Store shop ID in DB
        const { data: success, error: activateShopifyStoreError } = await activateShopifyStore(organization.id, shop);

        if (activateShopifyStoreError) {
            console.error(activateShopifyStoreError);
            setError('An unexpected error occurred. Please try again.');
            return;
        }

        // Redirect to integrations page
        navigate('/integrations/27', { replace: true });

      } catch (err) {
        console.error('Unexpected error in Shopify callback:', err);
        setError('An unexpected error occurred. Please try again.');
      }
    };

    handleCallback();
  }, [searchParams, navigate, loading, organization]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl mt-20 px-4">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error connecting Shopify store</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <Link
                    to="/integrations"
                    className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  >
                    Go to Integrations
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl mt-20 px-4 text-center">
      <Loading />
      <p className="mt-4 text-lg font-medium text-gray-700">Connecting your Shopify store...</p>
      <p className="mt-3 text-xl font-semibold text-gray-900">Connecting to Muffin...</p>
    </div>
  );
}
